import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "../api/client";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  promoteUser, resendUserAccess, RECOVERY_SENT_NOTE, RECOVERY_STALE_NOTE, accessStatusLabel,
  MFA_RESET_NOTE, MFA_SIGNOUT_FAILED_NOTE, SIGNED_OUT_NOTE,
  type AccessStatus, type RecoveryAction, type RecoveryResult,
} from "../api/portal";

interface TeamUser {
  user_id: string;
  email: string;
  role: "internal" | "customer_admin" | "customer_user";
  active: boolean;
  access_status: AccessStatus | null;
}

/**
 * Two modes over one list.
 *
 * A customer_admin manages their own team (invite / deactivate) but can
 * never change who holds customer_admin. An internal user sees the same
 * list read-only, plus the one action customers don't get: reassigning the
 * Customer Admin. See the Phase 2 spec §4.
 */
export function TeamPage({ clientId }: { clientId?: string } = {}) {
  const internal = Boolean(clientId);
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingPromote, setPendingPromote] = useState<TeamUser | null>(null);
  const [pendingReset, setPendingReset] = useState<TeamUser | null>(null);
  const [pendingMfaReset, setPendingMfaReset] = useState<TeamUser | null>(null);
  const [pendingSignOut, setPendingSignOut] = useState<TeamUser | null>(null);
  // A Set, not a single id: recovering one row must not make an earlier
  // row's "Sent" note vanish or its button reappear -- every resend
  // invalidates that user's previous temporary password, so the note is
  // load-bearing per-row, not a single most-recent-action flag.
  // A Map rather than a Set: the note depends on which action completed.
  const [sent, setSent] = useState<Map<string, RecoveryResult>>(new Map());

  // Which note to show depends on what was actually done -- a cleared
  // authenticator, a session ended, and a reissued temporary password have
  // nothing in common except that all three land in this cell.
  const noteFor = (r: RecoveryResult) => {
    if (r.action_taken === "reset_mfa") {
      return r.signed_out === false ? MFA_SIGNOUT_FAILED_NOTE : MFA_RESET_NOTE;
    }
    if (r.action_taken === "sign_out") return SIGNED_OUT_NOTE;
    return RECOVERY_SENT_NOTE;
  };

  const scope = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
  const teamKey = ["team", clientId ?? null];

  const { data, isLoading } = useQuery({
    queryKey: teamKey,
    queryFn: () => apiFetch<{ users: TeamUser[] }>(`/portal/users${scope}`),
  });

  const invite = useMutation({
    mutationFn: (email: string) => apiFetch("/portal/users", { method: "POST", body: JSON.stringify({ email }) }),
    onSuccess: () => {
      setEmail("");
      setError(null);
      queryClient.invalidateQueries({ queryKey: teamKey });
    },
    onError: (e) => {
      setError(
        e instanceof ApiError && e.status === 409
          ? "This email is already registered."
          : "Could not invite this user.",
      );
    },
  });

  const deactivate = useMutation({
    mutationFn: (userId: string) => apiFetch(`/portal/users/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: teamKey });
    },
    onError: () => setError("Could not deactivate this user."),
  });

  const promote = useMutation({
    mutationFn: (userId: string) => promoteUser(userId),
    onSuccess: () => {
      setError(null);
      setPendingPromote(null);
      queryClient.invalidateQueries({ queryKey: teamKey });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (e) => {
      setPendingPromote(null);
      setError(
        e instanceof ApiError && e.status === 409
          ? "Another admin change is in flight. Try again."
          : "Could not reassign the Customer Admin.",
      );
    },
  });

  const recover = useMutation({
    mutationFn: ({ user, action }: { user: TeamUser; action: RecoveryAction }) =>
      resendUserAccess(user.user_id, action),
    onSuccess: (result, { user }) => {
      setError(null);
      setPendingReset(null);
      setPendingMfaReset(null);
      setPendingSignOut(null);
      // Replace, not mutate -- React only re-renders on a new Map identity.
      setSent((prev) => new Map(prev).set(user.user_id, result));
      queryClient.invalidateQueries({ queryKey: teamKey });
    },
    onError: (e) => {
      setPendingReset(null);
      setPendingMfaReset(null);
      setPendingSignOut(null);
      setError(
        e instanceof ApiError && e.status === 409
          ? RECOVERY_STALE_NOTE
          : "Could not send that email.",
      );
    },
  });

  const users = data?.users ?? [];
  const currentAdmin = users.find((u) => u.role === "customer_admin" && u.active);

  return (
    <div>
      <h2>Team</h2>

      {!internal && (
        <form
          onSubmit={(e) => { e.preventDefault(); invite.mutate(email); }}
          style={{ display: "flex", gap: 8, margin: "12px 0" }}
        >
          <input
            type="email"
            placeholder="Invite by email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button className="btn-primary" type="submit" disabled={invite.isPending}>Invite</button>
        </form>
      )}
      {internal && (
        <p className="dim">
          Read-only. Inviting and removing users is the customer's own action — the one
          thing you can do here is hand the Customer Admin designation to someone else.
        </p>
      )}

      {error && <p style={{ color: "var(--red)" }}>{error}</p>}
      {isLoading && <p>Loading…</p>}
      <table className="data-table">
        <thead><tr><th>Email</th><th>Role</th><th>Status</th><th /></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id}>
              <td>{u.email}</td>
              <td>
                {u.role === "customer_admin"
                  ? <span className="badge ok">Customer Admin</span>
                  : u.role}
              </td>
              <td>
                {u.active ? "Active" : "Deactivated"}
                {u.active && accessStatusLabel(u.access_status) && (
                  <><br /><span className="dim">{accessStatusLabel(u.access_status)}</span></>
                )}
              </td>
              <td>
                {!internal && u.role === "customer_user" && u.active && (
                  <button onClick={() => deactivate.mutate(u.user_id)} disabled={deactivate.isPending}>Deactivate</button>
                )}
                {internal && u.role === "customer_user" && u.active && (
                  <button onClick={() => setPendingPromote(u)} disabled={promote.isPending}>
                    Promote to Customer Admin
                  </button>
                )}
                {/* Customer-side only: a customer_admin recovers its own team.
                    Internal staff see this list read-only. */}
                {!internal && u.role === "customer_user" && u.active && !sent.has(u.user_id)
                  && u.access_status === "pending_first_login" && (
                  <button
                    disabled={recover.isPending}
                    onClick={() => recover.mutate({ user: u, action: "resend_invite" })}
                  >
                    Resend invitation
                  </button>
                )}
                {!internal && u.role === "customer_user" && u.active && !sent.has(u.user_id)
                  && u.access_status === "active" && (
                  <button disabled={recover.isPending} onClick={() => setPendingReset(u)}>
                    Send password reset
                  </button>
                )}
                {/* Not gated on whether a factor is registered -- see StaffPage. */}
                {!internal && u.role === "customer_user" && u.active
                  && u.access_status === "active" && !sent.has(u.user_id) && (
                  <button type="button" disabled={recover.isPending}
                          onClick={() => setPendingMfaReset(u)}>
                    Reset MFA
                  </button>
                )}
                {/* Not suppressed by `sent`: this is the retry path for a reset
                    that deleted the factor and then failed to end the session,
                    so the row is already marked as acted-on exactly when the
                    button is needed. */}
                {!internal && u.role === "customer_user" && u.active
                  && u.access_status === "active" && (
                  <button type="button" disabled={recover.isPending}
                          onClick={() => setPendingSignOut(u)}>
                    Sign out
                  </button>
                )}
                {sent.has(u.user_id) && (
                  <span className="dim">{noteFor(sent.get(u.user_id)!)}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pendingPromote && (
        <ConfirmDialog
          title="Reassign the Customer Admin?"
          confirmLabel="Reassign"
          busy={promote.isPending}
          consequences={[
            <><strong>{pendingPromote.email}</strong> becomes the Customer Admin and can manage this team.</>,
            currentAdmin
              ? <><strong>{currentAdmin.email}</strong> becomes a regular user and loses team management.</>
              : "This client currently has no active Customer Admin.",
            "There is exactly one admin per client — this is a handoff, not an addition.",
          ]}
          onCancel={() => setPendingPromote(null)}
          onConfirm={() => promote.mutate(pendingPromote.user_id)}
        />
      )}

      {pendingReset && (
        <ConfirmDialog
          title={`Send a password reset to ${pendingReset.email}?`}
          confirmLabel="Send reset email"
          busy={recover.isPending}
          consequences={[
            "They receive a code by email and choose a new password.",
            "Their current password stops working immediately.",
          ]}
          onCancel={() => setPendingReset(null)}
          onConfirm={() => recover.mutate({ user: pendingReset, action: "reset_password" })}
        />
      )}

      {pendingMfaReset && (
        <ConfirmDialog
          title={`Clear ${pendingMfaReset.email}'s authenticator?`}
          tone="danger"
          confirmLabel="Clear authenticator"
          busy={recover.isPending}
          consequences={[
            "Their registered authenticator is deleted and cannot be restored.",
            "They are signed out, and enroll a new device at their next login.",
            "A page they already have open may keep working for up to an hour.",
            "Their password is unchanged.",
          ]}
          onCancel={() => setPendingMfaReset(null)}
          onConfirm={() => recover.mutate({ user: pendingMfaReset, action: "reset_mfa" })}
        />
      )}

      {pendingSignOut && (
        <ConfirmDialog
          title={`Sign ${pendingSignOut.email} out of the portal?`}
          confirmLabel="Sign this user out"
          busy={recover.isPending}
          consequences={[
            "Their open sessions stop working and they return to the login page.",
            "A page they already have open may keep working for up to an hour.",
            "Their password and authenticator are unchanged.",
          ]}
          onCancel={() => setPendingSignOut(null)}
          onConfirm={() => recover.mutate({ user: pendingSignOut, action: "sign_out" })}
        />
      )}
    </div>
  );
}
